import {
  VenuePortalNotification,
  VenuePortalNotificationTranslation,
  VenuePortalNotificationUserLink,
} from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useUser from "@/hooks/useUser";
import { Chip, Divider, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import NotificationsListSkeleton from "./NotificationsListSkeleton";

type NotificationsListProps = {
  notificationLinks: (Pick<
    VenuePortalNotificationUserLink,
    "venuePortalNotificationId"
  > & {
    readAt: VenuePortalNotificationUserLink["readAt"];
    notification: {
      created: VenuePortalNotification["created"];
      translation: Pick<
        VenuePortalNotificationTranslation,
        "body" | "subject"
      > | null;
    };
  })[];
  loading?: boolean;
};

export default function NotificationsList({
  notificationLinks,
  loading = false,
}: NotificationsListProps) {
  const { t, adapterLocale } = useLocalization();
  const { markNotificationsRead } = useUser();
  const notificationLinksWithTranslation = useMemo(
    () =>
      notificationLinks.filter(
        (notificationLink) =>
          notificationLink.notification.translation !== null,
      ),
    [notificationLinks],
  );

  const unreadIds = useMemo(
    () =>
      notificationLinks
        .filter((notification) => !notification.readAt)
        .map((notification) => notification.venuePortalNotificationId),
    [notificationLinks],
  );

  const newNotificationIdsRef = useRef<Set<string> | null>(null);

  if (!newNotificationIdsRef.current && unreadIds.length)
    newNotificationIdsRef.current = new Set(unreadIds);

  const newNotificationIds = newNotificationIdsRef.current ?? new Set();

  const hasMarkedRead = useRef(false);

  useEffect(() => {
    if (hasMarkedRead.current) return;
    if (unreadIds.length === 0) return;
    markNotificationsRead();
    hasMarkedRead.current = true;
  }, [markNotificationsRead, unreadIds.length]);

  if (loading) return <NotificationsListSkeleton />;

  if (notificationLinksWithTranslation.length === 0) {
    return (
      <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
        <Typography variant="h6">{t("notification:empty.title")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("notification:empty.body")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {notificationLinksWithTranslation.map((notificationLink, index) => (
        <Stack key={notificationLink.venuePortalNotificationId} gap={1}>
          <Stack direction="row" gap={1} alignItems="center">
            {newNotificationIds.has(
              notificationLink.venuePortalNotificationId,
            ) && (
              <Chip
                size="small"
                label="new"
                sx={{ backgroundColor: "warning.main", color: "black" }}
              />
            )}
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", fontSize: 18 }}
            >
              {notificationLink.notification.translation?.subject}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", flexGrow: 1, textAlign: "end" }}
            >
              {format(new Date(notificationLink.notification.created), "PP", {
                locale: adapterLocale,
              })}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{
              __html: notificationLink.notification.translation?.body ?? "",
            }}
          />
          {index < notificationLinksWithTranslation.length - 1 && <Divider />}
        </Stack>
      ))}
    </Stack>
  );
}
